/**
 * MergePanel - Slide drawer for manually merging two canonical fish species
 * Source canonical will be deleted; target canonical will receive combined data
 */
import { useState, useEffect } from 'react';
import {
  Drawer,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Alert,
  Loader,
  Center,
  Divider,
  ScrollArea,
  TextInput,
  Paper,
  Badge,
} from '@mantine/core';
import { IconAlertCircle, IconGitMerge, IconSearch } from '@tabler/icons-react';
import { fishSpeciesApi } from '@/api/fishSpecies';
import type { FishCanonicalWithSources } from '@/types/livestock';
import { FieldComparisonRow } from './FieldComparisonRow';

// Fields that can be customized during merge
const MERGEABLE_FIELDS = [
  { name: 'common_name', label: 'Common Name' },
  { name: 'scientific_name', label: 'Scientific Name' },
  { name: 'family', label: 'Family' },
  { name: 'category', label: 'Category' },
  { name: 'care_level', label: 'Care Level' },
  { name: 'temperament', label: 'Temperament' },
  { name: 'diet', label: 'Diet' },
  { name: 'reef_compatible', label: 'Reef Compatible' },
  { name: 'max_size_inches', label: 'Max Size (inches)' },
  { name: 'min_tank_size_gallons', label: 'Min Tank Size (gallons)' },
  { name: 'description', label: 'Description' },
  { name: 'image_url', label: 'Image URL' },
] as const;

type MergeableFieldName = (typeof MERGEABLE_FIELDS)[number]['name'];

interface MergePanelProps {
  opened: boolean;
  onClose: () => void;
  sourceCanonicalId: number;
  onSuccess: (mergedCanonicalId: number) => void;
}

export function MergePanel({
  opened,
  onClose,
  sourceCanonicalId,
  onSuccess,
}: MergePanelProps) {
  const [sourceLoading, setSourceLoading] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [sourceCanonical, setSourceCanonical] = useState<FishCanonicalWithSources | null>(null);
  const [targetCanonical, setTargetCanonical] = useState<FishCanonicalWithSources | null>(null);
  const [targetIdInput, setTargetIdInput] = useState('');
  const [fieldSelections, setFieldSelections] = useState<Record<string, 'source' | 'target'>>({});

  // Load source canonical when opened
  useEffect(() => {
    if (opened && sourceCanonicalId) {
      setSourceLoading(true);
      setError(null);

      fishSpeciesApi
        .getCanonicalWithSources(sourceCanonicalId)
        .then((data) => {
          setSourceCanonical(data);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load source canonical data');
        })
        .finally(() => {
          setSourceLoading(false);
        });
    }
  }, [opened, sourceCanonicalId]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!opened) {
      setSourceCanonical(null);
      setTargetCanonical(null);
      setTargetIdInput('');
      setFieldSelections({});
      setError(null);
    }
  }, [opened]);

  const handleLoadTarget = async () => {
    const targetId = parseInt(targetIdInput, 10);
    if (isNaN(targetId) || targetId <= 0) {
      setError('Please enter a valid canonical ID');
      return;
    }

    if (targetId === sourceCanonicalId) {
      setError('Target cannot be the same as source');
      return;
    }

    setTargetLoading(true);
    setError(null);

    try {
      const data = await fishSpeciesApi.getCanonicalWithSources(targetId);
      setTargetCanonical(data);
      // Pre-select target values for all fields
      const initialSelections: Record<string, 'source' | 'target'> = {};
      for (const field of MERGEABLE_FIELDS) {
        initialSelections[field.name] = 'target';
      }
      setFieldSelections(initialSelections);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load target canonical');
    } finally {
      setTargetLoading(false);
    }
  };

  const handleFieldSelect = (fieldName: string, source: string) => {
    setFieldSelections((prev) => ({
      ...prev,
      [fieldName]: source as 'source' | 'target',
    }));
  };

  const handleConfirmMerge = async () => {
    if (!sourceCanonical || !targetCanonical) return;

    setSubmitting(true);
    setError(null);

    try {
      const response = await fishSpeciesApi.manualMerge({
        source_canonical_id: sourceCanonical.canonical_id,
        target_canonical_id: targetCanonical.canonical_id,
        field_selections: fieldSelections,
      });
      onSuccess(response.merged_canonical_id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge canonicals');
    } finally {
      setSubmitting(false);
    }
  };

  // Build field values for comparison rows
  const getFieldValues = (fieldName: MergeableFieldName) => {
    if (!sourceCanonical || !targetCanonical) return [];

    return [
      {
        source: 'source',
        value: sourceCanonical[fieldName],
        label: 'Source (will be deleted)',
      },
      {
        source: 'target',
        value: targetCanonical[fieldName],
        label: 'Target (will remain)',
      },
    ];
  };

  const renderSourceInfo = () => {
    if (sourceLoading) {
      return (
        <Center h={100} data-testid="source-loading">
          <Loader size="sm" />
        </Center>
      );
    }

    if (!sourceCanonical) return null;

    return (
      <Paper p="md" bg="red.1" withBorder data-testid="source-info">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600}>Source Canonical</Text>
            <Badge color="red" variant="filled">
              Will be deleted
            </Badge>
          </Group>
          <Text size="sm">
            <strong>ID:</strong> {sourceCanonical.canonical_id}
          </Text>
          <Text size="sm">
            <strong>Common Name:</strong> {sourceCanonical.common_name || 'N/A'}
          </Text>
          <Text size="sm">
            <strong>Scientific Name:</strong> {sourceCanonical.scientific_name || 'N/A'}
          </Text>
          <Text size="sm" c="dimmed">
            {sourceCanonical.source_count} linked source(s)
          </Text>
        </Stack>
      </Paper>
    );
  };

  const renderTargetInput = () => {
    if (targetCanonical) return null;

    return (
      <Paper p="md" withBorder data-testid="target-input-section">
        <Stack gap="sm">
          <Text fw={600}>Target Canonical</Text>
          <Text size="sm" c="dimmed">
            Enter the ID of the canonical species you want to merge into.
          </Text>
          <Group gap="sm">
            <TextInput
              placeholder="Enter canonical ID..."
              value={targetIdInput}
              onChange={(e) => setTargetIdInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && targetIdInput.trim()) {
                  handleLoadTarget();
                }
              }}
              style={{ flex: 1 }}
              data-testid="target-id-input"
            />
            <Button
              leftSection={<IconSearch size={16} />}
              onClick={handleLoadTarget}
              loading={targetLoading}
              data-testid="load-target-button"
            >
              Load
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  };

  const renderTargetInfo = () => {
    if (!targetCanonical) return null;

    return (
      <Paper p="md" bg="green.1" withBorder data-testid="target-info">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text fw={600}>Target Canonical</Text>
            <Badge color="green" variant="filled">
              Will remain
            </Badge>
          </Group>
          <Text size="sm">
            <strong>ID:</strong> {targetCanonical.canonical_id}
          </Text>
          <Text size="sm">
            <strong>Common Name:</strong> {targetCanonical.common_name || 'N/A'}
          </Text>
          <Text size="sm">
            <strong>Scientific Name:</strong> {targetCanonical.scientific_name || 'N/A'}
          </Text>
          <Text size="sm" c="dimmed">
            {targetCanonical.source_count} linked source(s)
          </Text>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => {
              setTargetCanonical(null);
              setFieldSelections({});
            }}
          >
            Change target
          </Button>
        </Stack>
      </Paper>
    );
  };

  const renderFieldComparison = () => {
    if (!sourceCanonical || !targetCanonical) return null;

    return (
      <>
        <Divider label="Field Values" labelPosition="center" />

        <Alert color="blue" variant="light">
          <Text size="sm">
            Select which values to keep for the merged record. The source canonical will be
            deleted and its sources will be linked to the target.
          </Text>
        </Alert>

        <ScrollArea.Autosize mah="calc(100vh - 600px)" offsetScrollbars>
          <Stack gap="xs">
            {MERGEABLE_FIELDS.map((field) => (
              <FieldComparisonRow
                key={field.name}
                fieldName={field.name}
                fieldLabel={field.label}
                values={getFieldValues(field.name)}
                selectedSource={fieldSelections[field.name] || 'target'}
                onSelect={(source) => handleFieldSelect(field.name, source)}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </>
    );
  };

  const renderContent = () => {
    if (error && !sourceCanonical) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          data-testid="error-state"
        >
          {error}
        </Alert>
      );
    }

    return (
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            onClose={() => setError(null)}
            withCloseButton
            data-testid="error-alert"
          >
            {error}
          </Alert>
        )}

        {renderSourceInfo()}
        {renderTargetInput()}
        {renderTargetInfo()}
        {renderFieldComparison()}

        <Divider />

        <Group justify="flex-end" gap="md">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="blue"
            leftSection={<IconGitMerge size={16} />}
            onClick={handleConfirmMerge}
            loading={submitting}
            disabled={!sourceCanonical || !targetCanonical}
            data-testid="confirm-merge-button"
          >
            Confirm Merge
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Group gap="sm">
          <IconGitMerge size={20} />
          <Title order={4}>Merge Canonical Species</Title>
        </Group>
      }
      padding="md"
    >
      {renderContent()}
    </Drawer>
  );
}
